import axios from "axios";
import { handleActions } from "redux-actions";

import Config from "config";
import { getHeaders } from "helpers/headers";
import { AnyAction } from "interfaces/action";
import { Chain } from "interfaces/chain";
import { Dispatch } from "interfaces/dispatch";
import { ThunkActionCreator } from "interfaces/thunk";

// Action Types
const FETCH_LOGGED_USER_CHAINS_REQUEST = "FETCH_LOGGED_USER_CHAINS_REQUEST";
const FETCH_LOGGED_USER_CHAINS = "FETCH_LOGGED_USER_CHAINS";
const FETCH_LOGGED_USER_CHAINS_FAILED = "FETCH_LOGGED_USER_CHAINS_FAILED";
const FETCH_CHAIN_PROJECTS_REQUEST = "FETCH_CHAIN_PROJECTS_REQUEST";
const FETCH_CHAIN_PROJECTS_SUCCESS = "FETCH_CHAIN_PROJECTS_SUCCESS";
const FETCH_CHAIN_PROJECTS_FAILED = "FETCH_CHAIN_PROJECTS_FAILED";

export {
  FETCH_LOGGED_USER_CHAINS_REQUEST,
  FETCH_LOGGED_USER_CHAINS,
  FETCH_LOGGED_USER_CHAINS_FAILED,
  FETCH_CHAIN_PROJECTS_REQUEST,
  FETCH_CHAIN_PROJECTS_SUCCESS,
  FETCH_CHAIN_PROJECTS_FAILED,
};

export interface Project {
  // Define project properties here (e.g., id, name, status, etc.)
  project_id: number;
  project_name: string;
  chain_name: string;
  // ... other project properties
}

// Thunk Action Creator
const fetchLoggedUserChains: ThunkActionCreator<Promise<void>> = () => (dispatch: Dispatch) => {
  dispatch({ type: FETCH_LOGGED_USER_CHAINS_REQUEST });

  return axios
    .get(`${Config.API_SERVICE_URL}/api/logged-user-chains`, getHeaders())
    .then((response) => {
      dispatch({
        type: FETCH_LOGGED_USER_CHAINS,
        payload: response.data,
      });
    })
    .catch((error: Error) => {
      dispatch({ type: FETCH_LOGGED_USER_CHAINS_FAILED });
    });
};

const fetchChainProjects: ThunkActionCreator<Promise<void>> =
  (chainId: number) => (dispatch: Dispatch) => {
    dispatch({ type: FETCH_CHAIN_PROJECTS_REQUEST });

    return axios
      .get(
        `${Config.API_SERVICE_URL}/api/logged-user-chains/my_chains_projects/${chainId}`,
        getHeaders()
      )
      .then((response) => {
        dispatch({
          type: FETCH_CHAIN_PROJECTS_SUCCESS,
          payload: response.data, // This should be an array of projects
        });
      })
      .catch((error: Error) => {
        dispatch({ type: FETCH_CHAIN_PROJECTS_FAILED });
      });
  };

export { fetchLoggedUserChains, fetchChainProjects };

// Reducer Interface
export interface LoggedUserChainsState {
  error?: boolean;
  loading?: boolean;
  content?: Chain[]; // likely multiple chains
  projectsLoading?: boolean;
  projectsError?: boolean;
  chainProjects?: Project[];
}

// Initial State
const initialState: LoggedUserChainsState = {
  loading: false,
  error: false,
  content: undefined,
  projectsLoading: false,
  projectsError: false,
  chainProjects: undefined,
};

// Actions
const actions = {
  [FETCH_LOGGED_USER_CHAINS_REQUEST]: (state: LoggedUserChainsState) => ({
    ...state,
    loading: true,
    error: false,
  }),

  [FETCH_LOGGED_USER_CHAINS]: (
    state: LoggedUserChainsState,
    action: AnyAction
  ): LoggedUserChainsState => ({
    ...state,
    loading: false,
    content: action.payload,
  }),

  [FETCH_LOGGED_USER_CHAINS_FAILED]: (state: LoggedUserChainsState): LoggedUserChainsState => ({
    ...state,
    loading: false,
    error: true,
    content: undefined,
  }),

  [FETCH_CHAIN_PROJECTS_REQUEST]: (state: LoggedUserChainsState) => ({
    ...state,
    projectsLoading: true,
    projectsError: false,
    chainProjects: undefined,
  }),

  [FETCH_CHAIN_PROJECTS_SUCCESS]: (
    state: LoggedUserChainsState,
    action: AnyAction
  ): LoggedUserChainsState => ({
    ...state,
    projectsLoading: false,
    chainProjects: action.payload,
  }),

  [FETCH_CHAIN_PROJECTS_FAILED]: (state: LoggedUserChainsState): LoggedUserChainsState => ({
    ...state,
    projectsLoading: false,
    projectsError: true,
    chainProjects: undefined,
  }),
};

// Reducer
export default handleActions<LoggedUserChainsState, AnyAction>(actions, initialState);
